import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

/**
 * Inline rename hook — generic & store-agnostic.
 *
 * @param {string} initial     initial label
 * @param {(newText:string)=>void} onRename   callback → API
 * @param {boolean} editing    is this item currently in edit mode?
 * @param {() => void} exitEdit tell caller we’re done (blur / Enter)
 */
export function useInlineRename(initial, onRename, editing, exitEdit) {
  const [text, setText] = useState(initial)
  const ref = useRef(null)
  const original = useRef(initial)

  // focus when entering edit mode
  useLayoutEffect(() => {
    if (editing && ref.current) {
      requestAnimationFrame(() => {
        const el = ref.current
        el?.focus()

        const sel = window.getSelection()
        if (sel && el.firstChild) {
          const range = document.createRange()
          range.selectNodeContents(el)
          sel.removeAllRanges()
          sel.addRange(range)
        }
      })
    }
  }, [editing])

  const finish = useCallback(() => {
    if (!editing) return

    const newText = ref.current?.innerText.replace(/\s+/g, ' ').trim() || ''

    exitEdit()

    if (newText === original.current) {
      if (ref.current) ref.current.innerText = original.current
      return
    }

    onRename(newText)
      .then(res => {
        const saved = res.body?.title ?? newText
        original.current = saved
        setText(saved)
        if (ref.current) ref.current.innerText = saved
      })
      .catch(() => {
        const old = original.current
        setText(old)
        if (ref.current) {
          ref.current.innerText = old
          const sel = window.getSelection()
          sel?.collapse(ref.current, 1)
        }
      })
  }, [editing, onRename, exitEdit])

  useEffect(() => {
    if (!editing) return
    const onClick = e => {
      if (ref.current && !ref.current.contains(e.target)) finish()
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [editing, finish])

  useEffect(() => {
    if (!editing) return
    const onKey = e => e.key === 'Escape' && finish()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [editing, finish])

  return {
    text,
    props: editing
      ? {
          contentEditable: true,
          suppressContentEditableWarning: true,
          ref,
          onBlur: finish,
          onKeyDown: e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              finish()
            }
          },
          tabIndex: -1,
          onPaste: e => {
            e.preventDefault()
            const text = e.clipboardData.getData('text/plain')

            const sel = window.getSelection()
            if (!sel || !sel.rangeCount) return

            // remove current selection contents
            sel.deleteFromDocument()

            // insert plaintext node at caret
            sel.getRangeAt(0).insertNode(document.createTextNode(text))

            // move caret to end of inserted text
            sel.collapseToEnd()
          },
        }
      : { ref, tabIndex: -1 },
  }
}
